import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AdminApiService } from '../services/apiClient'
import {
  GetNotificationsParams,
  AdminNotificationCategory,
  AdminNotificationSocketEvent,
  NewAdminNotificationPayload,
  AdminNotificationReadPayload,
  AdminNotificationDeletedPayload,
} from '@tenant/shared'
import { useEffect, useMemo } from 'react'
import { adminSocketService } from '../services/socketService'

/**
 * React Query keys for notifications
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: GetNotificationsParams) => [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
}

/**
 * Hook for fetching notifications with filters
 */
export function useNotifications(params: GetNotificationsParams = {}) {
  // Memoize params to prevent unnecessary refetches caused by object recreation
  const memoizedParams = useMemo(() => params, [
    params.page,
    params.limit,
    params.category,
    params.isRead,
    params.isUrgent,
  ])

  return useQuery({
    queryKey: notificationKeys.list(memoizedParams),
    queryFn: () => AdminApiService.getNotifications(memoizedParams),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Hook for getting unread notification count
 * ✅ PHASE 3: Optimized - disables polling when socket is connected
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await AdminApiService.getNotifications({ isRead: false, limit: 1 })
      return response.unreadCount
    },
    staleTime: 10000, // 10 seconds
    // ✅ OPTIMIZED: Disable polling when socket is connected (real-time updates)
    refetchInterval: () => adminSocketService.connected ? false : 30000,
  })
}

/**
 * Hook for marking notification as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => AdminApiService.markNotificationAsRead(notificationId),
    onSuccess: () => {
      // Invalidate all notification queries to refresh the data
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (category?: AdminNotificationCategory) =>
      AdminApiService.markAllNotificationsAsRead(category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook for deleting notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => AdminApiService.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook for real-time notification updates via Socket.IO
 * Automatically subscribes to notification events and updates cache
 */
export function useNotificationSocketUpdates() {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Handler for new notifications
    const handleNewNotification = (payload: NewAdminNotificationPayload) => {
      console.log('[Notifications] New notification received', payload)

      // Invalidate all notification queries to fetch fresh data
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    }

    // Handler for notification read
    const handleNotificationRead = (payload: AdminNotificationReadPayload) => {
      console.log('[Notifications] Notification marked as read', payload)

      // Invalidate queries to refresh read status
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    }

    // Handler for notification deleted
    const handleNotificationDeleted = (payload: AdminNotificationDeletedPayload) => {
      console.log('[Notifications] Notification deleted', payload)

      // Invalidate queries to remove deleted notification
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    }

    // Subscribe to socket events
    const socket = adminSocketService.getSocket()
    if (socket) {
      socket.on(AdminNotificationSocketEvent.NEW_NOTIFICATION, handleNewNotification)
      socket.on(AdminNotificationSocketEvent.NOTIFICATION_READ, handleNotificationRead)
      socket.on(AdminNotificationSocketEvent.NOTIFICATION_DELETED, handleNotificationDeleted)

      console.log('[Notifications] Subscribed to notification socket events')
    }

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.off(AdminNotificationSocketEvent.NEW_NOTIFICATION, handleNewNotification)
        socket.off(AdminNotificationSocketEvent.NOTIFICATION_READ, handleNotificationRead)
        socket.off(AdminNotificationSocketEvent.NOTIFICATION_DELETED, handleNotificationDeleted)
        console.log('[Notifications] Unsubscribed from notification socket events')
      }
    }
  }, [queryClient])
}

/**
 * All-in-one hook for notifications with real-time updates
 * Use this in your components for complete notification functionality
 */
export function useNotificationsWithRealtime(params: GetNotificationsParams = {}) {
  // Memoize params to prevent unnecessary refetches
  const memoizedParams = useMemo(() => params, [
    params.page,
    params.limit,
    params.category,
    params.isRead,
    params.isUrgent,
  ])

  useNotificationSocketUpdates()
  return useNotifications(memoizedParams)
}

